import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import {
  createCategoryByType,
  deleteCategoryById,
  getCategoriesWoLoader,
  updateCategoryActiveById,
  updateCategoryByTypeAndId,
} from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { delBtn, plusBtn } from "../../../util/CommonIconStyle";
import { patchMandateSession } from "../../../util/mandateSessionStorage";

const initValues = { name: "" };
const initErrs = { name: false };
const initHlps = { name: "" };

const tableHeaders = ["name", "activeStatus", "extraStatus"];

const toCategoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const LocationCategory = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [modVsble, setModVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [statusModVsble, setStatusModVsble] = useState(false);
  const [categoryValues, setCategoryValues] = useState(initValues);
  const [categoryErrors, setCategoryErrors] = useState(initErrs);
  const [categoryHelps, setCategoryHelps] = useState(initHlps);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pendingActiveStatus, setPendingActiveStatus] = useState(null);
  const [mandateTick, setMandateTick] = useState(0);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const categoriesByType = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const list = Array.isArray(reqData?.categories) ? reqData.categories : [];
      return list.filter((loc) => loc?.type === "location");
    } catch {
      return [];
    }
  }, [mandateDataRaw, mandateTick]);

  useEffect(() => {
    onApiCall();
  }, [dispatch]);

  const onApiCall = () => {
    try {
      dispatch(getCategoriesWoLoader()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          patchMandateSession({
            categories: toCategoryList(payload),
          });
          setMandateTick((t) => t + 1);
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore location refresh errors
    }
  };

  const handleCreate = () => {
    setModVsble(true);
  };

  const handleModClose = () => {
    setModVsble(false);
    setCategoryValues(initValues);
    setCategoryErrors(initErrs);
    setCategoryHelps(initHlps);
    setSelectedItem(null);
  };

  const handleChange = (name, value) => {
    setCategoryValues({ ...categoryValues, [name]: value });
    setCategoryErrors({ ...categoryErrors, [name]: value?.length === 0 });
    setCategoryHelps({
      ...categoryHelps,
      [name]: value?.length === 0 ? "This is required field" : "",
    });
  };

  const handleEdit = (loc) => {
    const reqObj = { ...categoryValues, name: loc?.name };
    setCategoryValues(reqObj);
    setSelectedItem(loc);
    setModVsble(true);
  };

  const handleDelete = (loc) => {
    setSelectedItem(loc);
    setDelModVsble(true);
  };

  const handleChangeSwitch = (loc) => {
    if (!loc?.id) return;
    setSelectedItem(loc);
    setPendingActiveStatus(!isActiveRecord(loc));
    setStatusModVsble(true);
  };

  const handleCloseStatusMod = () => {
    setStatusModVsble(false);
    setPendingActiveStatus(null);
    setSelectedItem(null);
  };

  const handleConfirmStatusUpdate = () => {
    if (!selectedItem?.id || pendingActiveStatus == null) return;
    dispatch(
      updateCategoryActiveById({
        id: selectedItem.id,
        active: pendingActiveStatus === true,
      }),
    ).then((res) => {
      if (res?.payload) {
        onApiCall();
        handleCloseStatusMod();
      }
    });
  };

  const getTableCategories = () => {
    return categoriesByType
      ?.filter((loc) =>
        loc?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      )
      ?.map((loc) => ({
        name: loc?.name,
        activeStatus: (
          <div className="flex-row-ali-cen">
            <CustomSwitch
              value={isActiveRecord(loc)}
              onChange={() => handleChangeSwitch(loc)}
              greenToGray
            />
            <div style={{ width: 80 }} className="ml10">
              {isActiveRecord(loc) ? (
                <Typography className="green4 grn-bck status-card fs12 font-medium">
                  Active
                </Typography>
              ) : (
                <Typography className="red11 red-back status-card fs12 font-medium">
                  Inactive
                </Typography>
              )}
            </div>
          </div>
        ),
        extraStatus: (
          <div className="flex-row flex1-end">
            <div className="cursor" onClick={() => handleDelete(loc)}>
              <div className="flex-center">
                <DeleteOutlineIcon sx={delBtn} />
              </div>
              <Typography className="red3 fs8 txt-semi-bold text-align-center">
                Delete
              </Typography>
            </div>
            <div className={`cursor ml10`} onClick={() => handleEdit(loc)}>
              <div className="flex-center">
                <EditIcon sx={plusBtn} />
              </div>
              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                Edit
              </Typography>
            </div>
          </div>
        ),
      }));
  };

  const handleValidate = () => {
    let errObj = {};
    let hlpObj = {};
    Object.keys(categoryValues).forEach((key) => {
      if (categoryValues?.[key]?.length === 0 || !categoryValues?.[key]) {
        errObj = {
          ...errObj,
          [key]: true,
        };
        hlpObj = {
          ...hlpObj,
          [key]: "This is required field",
        };
      }
    });
    setCategoryErrors({ ...categoryErrors, ...errObj });
    setCategoryHelps({ ...categoryHelps, ...hlpObj });
    return Object.values(errObj)?.some((err) => err === true);
  };

  const handleSave = () => {
    if (handleValidate()) return;
    const data = { name: categoryValues?.name, type: "location" };
    if (selectedItem) {
      dispatch(updateCategoryByTypeAndId({ id: selectedItem?.id, data })).then(
        (res) => {
          if (res?.payload) {
            onApiCall();
            handleModClose();
          }
        },
      );
    } else {
      dispatch(createCategoryByType(data)).then((res) => {
        if (res?.payload) {
          onApiCall();
          handleModClose();
        }
      });
    }
  };

  const handleCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedItem(null);
  };

  const handleDeleteLocation = () => {
    dispatch(deleteCategoryById(selectedItem?.id)).then((res) => {
      if (res?.payload) {
        onApiCall();
        handleCloseDelMod();
      }
    });
  };

  return (
    <Container maxWidth="xl">
      <div className="back-white pv10">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="flex-row-center"
        >
          <div className="flex-row">
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ width: "400px" }}
            >
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightBlue2}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </div>
          <div className="w5Per ml20">
            <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
              <div>
                <CustomButton text="Create" smallBtn onClick={handleCreate} />
              </div>
            </Grid>
          </div>
        </Grid>

        <CustomTable
          columns={tableHeaders}
          datas={getTableCategories()}
          grey
          maxHeight="72vh"
        />
      </div>

      <CustomModal
        open={modVsble}
        onClose={handleModClose}
        header={selectedItem ? "Edit" : "Create"}
        modalWidth={30}
        rightModal
      >
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                name="name"
                fullWidth
                value={categoryValues?.name}
                error={categoryErrors?.name}
                helperText={categoryHelps?.name}
                handleChange={(e) => {
                  const val = e?.target?.value?.replace(/[^a-zA-Z0-9 ]/g, "");
                  handleChange("name", val);
                }}
                label="Name"
                required
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt10">
              <div>
                <CustomButton
                  text={selectedItem ? "Update" : "Submit"}
                  smallBtn
                  onClick={handleSave}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDelMod}
        header="Delete Category"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the category?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={handleDeleteLocation}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={statusModVsble}
        onClose={handleCloseStatusMod}
        header="Update Status"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to update the status?
        </Typography>
        <Typography className="txt-regular fs14 ml10 mt10">
          This will affect the existing inventory configurations.
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="flex1-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                onClick={handleConfirmStatusUpdate}
                smallBtn
                redBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseStatusMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </Container>
  );
};

export default LocationCategory;
